"use client";

import { useEffect, useCallback } from "react";

interface UseBackButtonGuardOptions {
  onBackAttempt: () => void;
  enabled?: boolean;
}

/**
 * Hook to intercept browser back button and trigger a custom callback (e.g., show a modal)
 * @param onBackAttempt - Callback to execute when back button is pressed
 * @param enabled - Whether the guard is active (default: true)
 */

export function useBackButtonGuard({
  onBackAttempt,
  enabled = true,
}: UseBackButtonGuardOptions) {
  const handlePopState = useCallback(() => {
    if (enabled) {
      // Push state back to prevent navigation
      window.history.pushState(null, "", window.location.href);
      onBackAttempt();
    }
  }, [onBackAttempt, enabled]);

  useEffect(() => {
    if (!enabled) return;

    // Push initial state to history
    window.history.pushState(null, "", window.location.href);
    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [enabled, handlePopState]);
}
